/*
*    CommandSentry.js
*    Javascript Module
*
*    Allows you to specify an arbitrary number of target DOM elements to execute specified callbacks when
*    corresponding sequences of commands are entered correctly. Each callback can have both a key sequence
*    and a touch sequence associated with it.
*
*    Key sequences are specified as arrays of keypress keycodes. See http://www.expandinghead.net/keycode.html
*    Note that IE does not appear to generate keypress events for arrow keys (tested in IE7 and IE9).
*    Touch Sequences are specified as arrays of single-character strings, with each character corresponding to a
*    tap or directional swipe. Valid commands include "t" (tap), "u" (swipe up), "r" (swipe right), "d" (swipe down),
*    and "l" (swipe left).
*
*    Tested in Firefox, Safari, Chrome, IE9, and IE7. Also tested on iOS and Android devices.
*
*    Method: addCommand(successCallback, keySequence, touchSequence)
*        successCallback: function to be executed when either sequence is entered correctly
*        keySequence: Array of keycodes representing keyboard commands.
*        touchSequence: Array of touch command identifiers representing taps and directional swipes on a touchscreen.
*
*    Makes this sentry track the specified key/touch sequence pair and execute the successCallback function if either sequence
*    is entered correctly with any targetElement in focus. Sequences should always be specified; use the empty array [] to disable
*    one or the other.
*
*    Method: addTargetElement(targetElement)
*        targetElement: The DOM element that listens for key and/or touch commands. If not specified, this method uses
*            the executing window's document object by default.
*
*    Registers this sentry with the target element so it can track commands entered with that element in focus. This method is
*    optimized to only register touch or key monitoring events if a corresponding non-empty sequence has been previously submitted
*    via addCommand(). Thus, it is recommended to add commands before adding target elements.
*
*    Method: enterKeyCode(keyCode)
*        keyCode: An integer representing a pressed key, typically in response to the javascript keypress event.
*
*    Compatibility method. Useful for manually updating the CommandSentry in existing systems where the html can't be modified,
*    but other existing javascript functions can.
*
*    Usage Note:
*    The sentry does not buffer input, but instead monitors the number of correct entries in the sequence. Furthermore, the same
*    incorrect entry that resets progress can be counted as the first correct entry of the next attempt. Essentially this means that
*    previous input can potentially impede your ability to "start" entering the sequence, but only if the sequence starts with a
*    repeating pattern. For example, if the sequence is "LLR", and you enter "LLLR", the third L interrupts the sequence and resets
*    progress to 1, even though the last three commands matched the sequence.
*
*/
var CommandSentry;

(function () {
    "use strict";

    // Assumes only one logical screen.
    // This allows touches to move between separate CommandSentry instances.
    var tX1 = 0,
        tX2 = 0,
        tY1 = 0,
        tY2 = 0,
        tIsTap = false,
        tCapture = false;

    CommandSentry = function () {
        this._keySequences = [];
        this._touchSequences = [];
        this._successCallbacks = [];
        this._keySeqCounters = [];
        this._touchSeqCounters = [];
    };

    CommandSentry.prototype = {
        addCommand: function (successCallback, keySequence, touchSequence) {
            this._keySequences.push(keySequence);
            this._keySeqCounters.push(0);
            this._touchSequences.push(touchSequence);
            this._touchSeqCounters.push(0);
            this._successCallbacks.push(successCallback);
        },

        addTargetElement: function (targetElement) {
            var i;

            // Use the target element if specified, otherwise, default to executing window's document
            if (typeof targetElement === "undefined") {
                targetElement = document;
            }

            // Register for relevant events only if there's a corresponding nonempty sequence
            for (i = 0; i < this._keySequences.length; i += 1) {
                if (this._keySequences[i].length > 0) {
                    this._addEvent(targetElement, "keypress", this._keypressCallback);
                    break;
                }
            }
            for (i = 0; i < this._touchSequences.length; i += 1) {
                if (this._touchSequences[i].length > 0) {
                    this._addEvent(targetElement, "touchstart", this._touchstartCallback);
                    this._addEvent(targetElement, "touchmove", this._touchmoveCallback);
                    this._addEvent(targetElement, "touchend", this._touchendCallback);
                    break;
                }
            }
        },

        enterKeyCode: function (keyCode) {
            var i;

            for (i = 0; i < this._keySequences.length; i += 1) {
                if (keyCode !== this._keySequences[i][this._keySeqCounters[i]]) {
                    this._keySeqCounters[i] = 0;
                }

                if (keyCode === this._keySequences[i][this._keySeqCounters[i]]) {
                    this._keySeqCounters[i] += 1;
                    if (this._keySeqCounters[i] === this._keySequences[i].length && this._keySeqCounters[i] > 0) {
                        this._keySeqCounters[i] = 0;
                        this._successCallbacks[i]();
                    }
                }
            }
        },

        // "Private" Methods
        _addEvent: function (targetElement, type, callback) {
            if (targetElement.addEventListener) {
                targetElement.addEventListener(type, CommandSentry.bind(this, callback), false);
            }
            else if (targetElement.attachEvent) {
                targetElement.attachEvent("on" + type, CommandSentry.bind(this, callback, targetElement.parentWindow.event));
            }
        },

        _evaluateTouchCommand: function () {
            var dx = tX2 - tX1,
                dy = tY2 - tY1,
                xdir = dx > 0 ? "r" : "l",
                ydir = dy > 0 ? "d" : "u",
                command = Math.abs(dx) > Math.abs(dy) ? xdir : ydir,
                i;

            command = tIsTap ? "t" : command;

            for (i = 0; i < this._touchSequences.length; i += 1) {
                if (command !== this._touchSequences[i][this._touchSeqCounters[i]]) {
                    this._touchSeqCounters[i] = 0;
                }

                if (command === this._touchSequences[i][this._touchSeqCounters[i]]) {
                    this._touchSeqCounters[i] += 1;

                    if (this._touchSeqCounters[i] === this._touchSequences[i].length && this._touchSeqCounters[i] > 0) {
                        this._touchSeqCounters[i] = 0;
                        this._successCallbacks[i]();
                    }
                }
            }
        },

        // Event Callbacks
        _keypressCallback: function (event) {
            // Firefox uses charCode for keys which result in visible characters. In such cases, keyCode is 0.
            var keyCode = event.keyCode !== 0 ? event.keyCode : event.charCode;

            this.enterKeyCode(keyCode);
        },

        _touchstartCallback: function (event) {
            tX1 = event.changedTouches[0].pageX;
            tY1 = event.changedTouches[0].pageY;
            tIsTap = true;
            tCapture = true;
        },

        _touchmoveCallback: function (event) {
            if (tCapture && event.touches.length === 1) {
                tX2 = event.changedTouches[0].pageX;
                tY2 = event.changedTouches[0].pageY;
                tIsTap = false;
                tCapture = false;
                this._evaluateTouchCommand();
            }
        },

        _touchendCallback: function () {
            if (tIsTap) {
                this._evaluateTouchCommand();
            }
        }
    };

    CommandSentry.bind = function (scope, fn) {
        return function () {
            fn.apply(scope, arguments);
        };
    };
}());
